import { useState, useEffect, useMemo, useRef, useCallback } from "react";
import {
  Box,
  Typography,
  TextField,
  MenuItem,
  Switch,
  FormControlLabel,
  Paper,
  Chip,
  Stack,
  Collapse,
  CircularProgress,
  Button,
  IconButton,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import axios from "axios";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
  CartesianGrid,
} from "recharts";

import { APP_API_V1_BASE } from "../../../config/api";
const API_BASE = `${APP_API_V1_BASE}`;

export default function EventsTab({
  alerts = [],
  autoRefresh,
  setAutoRefresh,
  headers,
  device,
  loadData,
}) {
  const [severityFilter, setSeverityFilter] = useState("all");
  const [now, setNow] = useState(Date.now());
  const [lastUpdated, setLastUpdated] = useState(null);
  const [expandedAlertId, setExpandedAlertId] = useState(null);
  const [trendMinutes, setTrendMinutes] = useState(30);

  // FIX 1: Keyed trend state — prevents stale data flash when switching cards
  const [trendState, setTrendState] = useState({
    alertId: null,
    data: [],
    loading: false,
    error: false,
  });

  const abortRef = useRef(null);

  useEffect(() => {
    const interval = setInterval(() => setNow(Date.now()), 30000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    setLastUpdated(new Date());
  }, [alerts]);

  useEffect(() => {
    return () => {
      if (abortRef.current) abortRef.current.abort();
    };
  }, []);

  const fetchTrend = useCallback(
    async (alert, minutes = 30) => {
      if (!device?.id || !alert?.metric_key) return;

      if (abortRef.current) abortRef.current.abort();
      abortRef.current = new AbortController();

      setTrendState({
        alertId: alert.id,
        data: [],
        loading: true,
        error: false,
      });

      try {
        const res = await axios.get(
          `${API_BASE}/devices/${device.id}/metrics/${encodeURIComponent(alert.metric_key)}/trend?minutes=${minutes}&page=1&page_size=500`,
          { headers, signal: abortRef.current.signal },
        );

        // FIX 2: Handle both old array response and new { data: [], pagination: {} } shape
        const points = Array.isArray(res.data)
          ? res.data
          : Array.isArray(res.data?.data)
            ? res.data.data
            : [];

        setTrendState({
          alertId: alert.id,
          data: points,
          loading: false,
          error: false,
        });
      } catch (err) {
        // FIX 3: axios.isCancel() misses AbortController — also check err.code
        if (axios.isCancel(err) || err.code === "ERR_CANCELED") return;
        console.error("Trend fetch failed", err);
        setTrendState({
          alertId: alert.id,
          data: [],
          loading: false,
          error: true,
        });
      }
    },
    [device?.id, headers],
  );

  const handleExpand = (alert) => {
    if (expandedAlertId === alert.id) {
      setExpandedAlertId(null);
      return;
    }
    setExpandedAlertId(alert.id);
    setTrendMinutes(30);
    fetchTrend(alert, 30);
  };

  const handleTrendWindowChange = (alert, minutes) => {
    setTrendMinutes(minutes);
    fetchTrend(alert, minutes);
  };

  const handleAcknowledge = async (e, alertId) => {
    e.stopPropagation();
    try {
      await axios.patch(
        `${API_BASE}/alerts/${alertId}/acknowledge`,
        {},
        { headers },
      );
      loadData();
    } catch (err) {
      console.error("Failed to acknowledge alert", err);
    }
  };

  const activeAlerts = useMemo(
    () => alerts.filter((a) => a.status === "active"),
    [alerts],
  );

  const filteredAlerts = useMemo(() => {
    if (severityFilter === "all") return activeAlerts;
    return activeAlerts.filter((a) => a.severity === severityFilter);
  }, [activeAlerts, severityFilter]);

  const severityCount = useMemo(
    () => ({
      critical: activeAlerts.filter((a) => a.severity === "critical").length,
      high: activeAlerts.filter((a) => a.severity === "high").length,
      warning: activeAlerts.filter((a) => a.severity === "warning").length,
      info: activeAlerts.filter((a) => a.severity === "info").length,
    }),
    [activeAlerts],
  );

  const colorMap = {
    critical: "#D32F2F",
    high: "#F57C00",
    warning: "#ED6C02",
    info: "#0288D1",
  };

  const formatDuration = (triggeredAt) => {
    const diffMs = now - new Date(triggeredAt);
    const minutes = Math.floor(diffMs / 60000);
    if (minutes < 60) return `${minutes} min`;
    const hours = Math.floor(minutes / 60);
    const remaining = minutes % 60;
    return `${hours}h ${remaining}m`;
  };

  const renderTrendChart = (alert) => {
    const isThisAlert = trendState.alertId === alert.id;

    return (
      <Collapse in={expandedAlertId === alert.id} unmountOnExit>
        <Box
          mt={2}
          pt={2}
          mb={6}
          sx={{ borderTop: "1px solid #f0f0f0" }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Time window chips */}
          <Box
            display="flex"
            alignItems="center"
            gap={1}
            mb={2}
            flexWrap="wrap"
          >
            <Typography
              variant="caption"
              color="text.secondary"
              fontWeight={600}
            >
              Window:
            </Typography>
            {[5, 15, 30, 60, 360].map((m) => (
              <Chip
                key={m}
                label={m < 60 ? `${m}m` : `${m / 60}h`}
                size="small"
                color={trendMinutes === m ? "primary" : "default"}
                variant={trendMinutes === m ? "filled" : "outlined"}
                onClick={() => handleTrendWindowChange(alert, m)}
                sx={{ cursor: "pointer" }}
              />
            ))}
            <IconButton
              size="small"
              onClick={() => fetchTrend(alert, trendMinutes)}
              title="Refresh"
            >
              <RefreshIcon fontSize="small" />
            </IconButton>
          </Box>

          {/* Fixed height container — this is the critical fix */}
          <Box sx={{ width: "100%", height: 220 }}>
            {!isThisAlert || trendState.loading ? (
              <Box display="flex" alignItems="center" gap={1} height="100%">
                <CircularProgress size={18} />
                <Typography variant="caption" color="text.secondary">
                  Loading trend data...
                </Typography>
              </Box>
            ) : trendState.error ? (
              <Box display="flex" alignItems="center" height="100%">
                <Typography variant="body2" color="error">
                  Failed to load.{" "}
                  <span
                    style={{ cursor: "pointer", textDecoration: "underline" }}
                    onClick={() => fetchTrend(alert, trendMinutes)}
                  >
                    Retry
                  </span>
                </Typography>
              </Box>
            ) : trendState.data.length === 0 ? (
              <Box display="flex" alignItems="center" height="100%">
                <Typography variant="body2" color="text.secondary">
                  No data in the last{" "}
                  {trendMinutes < 60
                    ? `${trendMinutes} minutes`
                    : `${trendMinutes / 60} hours`}
                  .
                </Typography>
              </Box>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <LineChart
                  data={trendState.data}
                  margin={{ top: 5, right: 20, left: 0, bottom: 5 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#f5f5f5" />
                  <XAxis
                    dataKey="timestamp"
                    tickFormatter={(t) =>
                      new Date(t).toLocaleTimeString([], {
                        hour: "2-digit",
                        minute: "2-digit",
                      })
                    }
                    tick={{ fontSize: 11 }}
                  />
                  <YAxis tick={{ fontSize: 11 }} domain={["auto", "auto"]} />
                  <Tooltip
                    labelFormatter={(t) => new Date(t).toLocaleString()}
                    formatter={(value) => [value, alert.metric_key]}
                  />
                  <Line
                    type="monotone"
                    dataKey="value"
                    stroke="#1976d2"
                    strokeWidth={2}
                    dot={false}
                    isAnimationActive={false}
                  />
                  {/* FIX 5: Guard threshold before rendering ReferenceLine */}
                  {alert.threshold != null && (
                    <ReferenceLine
                      y={Number(alert.threshold)}
                      stroke="#D32F2F"
                      strokeDasharray="4 4"
                      label={{
                        value: `Threshold: ${alert.threshold}`,
                        position: "insideTopRight",
                        fontSize: 11,
                        fill: "#D32F2F",
                      }}
                    />
                  )}
                </LineChart>
              </ResponsiveContainer>
            )}
          </Box>
        </Box>
      </Collapse>
    );
  };

  return (
    <Box>
      {/* FILTER BAR */}
      <Box mb={2} display="flex" gap={2} alignItems="center" flexWrap="wrap">
        <TextField
          select
          size="small"
          label="Severity"
          value={severityFilter}
          onChange={(e) => setSeverityFilter(e.target.value)}
          sx={{ minWidth: 160 }}
        >
          <MenuItem value="all">All</MenuItem>
          <MenuItem value="critical">Critical</MenuItem>
          <MenuItem value="high">High</MenuItem>
          <MenuItem value="warning">Warning</MenuItem>
          <MenuItem value="info">Info</MenuItem>
        </TextField>

        <FormControlLabel
          control={
            <Switch
              checked={autoRefresh}
              onChange={(e) => setAutoRefresh(e.target.checked)}
            />
          }
          label={autoRefresh ? "🟢 LIVE" : "⚪ PAUSED"}
        />

        {lastUpdated && (
          <Typography variant="caption" color="text.secondary">
            Last updated: {lastUpdated.toLocaleTimeString()}
          </Typography>
        )}
      </Box>

      {/* SEVERITY STRIP — clickable to filter */}
      <Box
  sx={{
    display: "grid",
    gap: 1.2,
    mb: 3,

    // ✅ responsive layout
    gridTemplateColumns: {
      xs: "1fr 1fr",   // mobile: 2 per row
      sm: "repeat(3, auto)",
      md: "repeat(4, auto)", // desktop: all in one row
    },

    alignItems: "center",
  }}
>
  {[
    {
      label: "Critical",
      key: "critical",
      bg: "#FDECEA",
      color: "#D32F2F",
    },
    { label: "High", key: "high", bg: "#FFF4E5", color: "#F57C00" },
    { label: "Warning", key: "warning", bg: "#FFF3E0", color: "#ED6C02" },
    { label: "Info", key: "info", bg: "#E3F2FD", color: "#0288D1" },
  ].map(({ label, key, bg, color }) => {
    const active = severityFilter === key;

    return (
      <Chip
        key={key}
        label={`${label}: ${severityCount[key]}`}
        size="small"
        onClick={() =>
          setSeverityFilter(active ? "all" : key)
        }
        sx={{
          width: "100%", // ✅ equal width on mobile
          justifyContent: "center",

          backgroundColor: active ? color : bg,
          color: active ? "#fff" : color,
          fontWeight: 600,
          cursor: "pointer",

          transition: "0.2s",

          "&:hover": {
            opacity: 0.85,
          },
        }}
      />
    );
  })}
</Box>

      {/* ALERT LIST */}
      {filteredAlerts.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: "center" }}>
          <Typography color="success.main" fontWeight={600}>
            🟢 System Healthy — No Active Alerts
          </Typography>
        </Paper>
      ) : (
        filteredAlerts.map((alert) => (
          <Paper
            key={alert.id}
            sx={{
              p: 2,
              mb: 4,
              borderLeft: `6px solid ${colorMap[alert.severity] || "#ccc"}`,
              cursor: "pointer",
              transition: "box-shadow 0.2s",
              "&:hover": { boxShadow: 3 },
            }}
            onClick={() => handleExpand(alert)}
          >
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="flex-start"
            >
              <Box>
                <Typography fontWeight={700} fontSize={15}>
                  {alert.metric_key}
                </Typography>

                <Typography variant="body2" mt={0.5}>
                  Value: <strong>{alert.value_at_trigger}</strong>
                  {alert.threshold != null && (
                    <span style={{ color: "#999", marginLeft: 8 }}>
                      (threshold: {alert.threshold})
                    </span>
                  )}
                </Typography>

                <Typography
                  variant="caption"
                  color="text.secondary"
                  display="block"
                  mt={0.5}
                >
                  Triggered: {new Date(alert.triggered_at).toLocaleString()}
                </Typography>

                <Typography
                  variant="caption"
                  color="text.secondary"
                  display="block"
                >
                  Active for:{" "}
                  <strong>{formatDuration(alert.triggered_at)}</strong>
                </Typography>
              </Box>

              <Stack alignItems="flex-end" spacing={1}>
                <Chip
                  label={alert.severity?.toUpperCase()}
                  size="small"
                  sx={{
                    backgroundColor: colorMap[alert.severity] || "#ccc",
                    color: "#fff",
                    fontWeight: 700,
                  }}
                />
                {alert.status === "active" && (
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={(e) => handleAcknowledge(e, alert.id)}
                  >
                    Acknowledge
                  </Button>
                )}
                <Typography variant="caption" color="text.secondary">
                  {expandedAlertId === alert.id
                    ? "▲ Hide trend"
                    : "▼ Show trend"}
                </Typography>
              </Stack>
            </Box>

            {renderTrendChart(alert)}
          </Paper>
        ))
      )}
    </Box>
  );
}
